import { Component, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ModalService } from '../../../../core/services/modal.service';
import { ButtonComponent } from '../../button/button.component';
import { FormFieldComponent } from '../form-field/form-field.component';
import { SelectComponent } from '../select/select.component';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { TextComponent } from '../../typography/text/text.component';

@Component({
  selector: 'app-upload-document-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    FormFieldComponent,
    SelectComponent,
    FileUploadComponent,
    TextComponent,
  ],
  templateUrl: './upload-document-modal.component.html',
})
export class UploadDocumentModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly fb = inject(FormBuilder);

  // Read configuration from modal state
  protected readonly modalData = this.modalService.state()?.data || {};
  protected readonly uploadPath = this.modalData.uploadPath || 'temp/';
  protected readonly typeOptions = this.modalData.typeOptions || [];
  protected readonly accept = this.modalData.accept || '';
  protected readonly allowedExtensions = this.modalData.allowedExtensions || [];
  protected readonly onSuccess = this.modalData.onSuccess;
  
  protected readonly isSaving = signal(false);
  protected readonly isUploading = signal(false);
  protected readonly uploadError = signal<string | null>(null);

  protected form: FormGroup;

  constructor() {
    this.form = this.fb.group({
      type: [this.typeOptions[0]?.value || '', Validators.required],
      fileUrl: [null, Validators.required],
    });
  }

  cancel(): void {
    this.modalService.close();
  }

  onUploadState(state: 'idle' | 'uploading' | 'success' | 'error'): void {
    this.isUploading.set(state === 'uploading');
    if (state === 'error') {
      this.uploadError.set('File upload failed.');
    } else {
      this.uploadError.set(null);
    }
  }

  save(): void {
    if (this.form.invalid || this.isUploading()) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const formData = this.form.getRawValue();

    if (this.onSuccess) {
      this.onSuccess({
        type: formData.type,
        url: formData.fileUrl,
      });
    }

    this.modalService.close();
  }
}
